import { useEffect } from 'react';
import { initRunClubGuide } from './runClubGuide';

/**
 * Initializes interactivity (tabs, FAQ, scroll reveal, TOC active state,
 * progress bar, inline script execution) for richly-formatted standalone
 * articles that ship as raw HTML via `dangerouslySetInnerHTML`.
 */
export function useStandaloneInteractivity(article, contentRef) {
  useEffect(() => {
    if (!article || article.layout !== 'standalone' || !contentRef.current) return;
    const root = contentRef.current;

    // Rewrite body/html selectors → scope to the standalone container
    root.querySelectorAll('style').forEach((style) => {
      style.textContent = style.textContent
        .replace(/\bbody\s*\{/g, '.standalone-article-root {')
        .replace(/\bhtml\s*\{/g, '.standalone-article-root {');
    });

    // Run Club guide interactivity (sortable table, filters, ratings, forms)
    if (root.querySelector('.nja-guide')) initRunClubGuide(root);

    // Brain Oxygen Habits Checklist — markup ships in the article HTML;
    // sanitize strips inline scripts so the meter logic is wired here.
    const o2check = root.querySelector('.na-o2check');
    if (o2check) {
      const boxes = o2check.querySelectorAll('input[type="checkbox"]');
      const fill = o2check.querySelector('.fill');
      const out = o2check.querySelector('.out');
      const msgs = [
        "Start with sleep: it's the biggest lever.",
        'A start. Pick one more habit to lock in this week.',
        'Building momentum. Sleep and training are the two that matter most.',
        'Solid foundation. Tighten up your evening routine.',
        "Strong. You're protecting the supply most nights.",
        'Nearly there. One habit to go.',
        "All six. You're running a brain-friendly routine.",
      ];
      const updateO2 = () => {
        let n = 0;
        boxes.forEach((b) => { if (b.checked) n += 1; });
        if (fill) fill.style.width = `${(n / 6) * 100}%`;
        if (out) out.textContent = `${n} of 6 habits. ${msgs[n]}`;
      };
      boxes.forEach((b) => b.addEventListener('change', updateO2));
    }

    const progressBar = root.querySelector('#spg-progress-bar') || root.querySelector('#progress');
    const handleScroll = () => {
      if (progressBar) {
        const pct = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        progressBar.style.width = pct + '%';
      }
      const tocLinks = root.querySelectorAll('.toc-item a');
      const sections = root.querySelectorAll('.content-section, .hero, .tabs-section');
      let current = '';
      sections.forEach((s) => { if (window.scrollY >= s.offsetTop - 100) current = s.id; });
      tocLinks.forEach((link) => {
        link.classList.remove('active');
        if (link.getAttribute('href') === '#' + current) link.classList.add('active');
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Tabs
    const tabBtns = root.querySelectorAll('[data-spg-tab]');
    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const goal = btn.dataset.spgTab;
        root.querySelectorAll('.tab-content').forEach((t) => t.classList.remove('active'));
        tabBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        if (goal === 'all') {
          const allTab = root.querySelector('#spg-tab-all');
          if (allTab) allTab.classList.add('active');
          root.querySelectorAll('#spg-peptide-grid-all .peptide-card').forEach((c) => { c.style.display = ''; });
          return;
        }
        const targetGrid = root.querySelector('#spg-peptide-grid-' + goal);
        const sourceCards = root.querySelectorAll('#spg-peptide-grid-all .peptide-card');
        if (targetGrid) {
          targetGrid.innerHTML = '';
          sourceCards.forEach((card) => {
            if (card.dataset.goal === goal) targetGrid.appendChild(card.cloneNode(true));
          });
        }
        const tabEl = root.querySelector('#spg-tab-' + goal);
        if (tabEl) tabEl.classList.add('active');
      });
    });

    // Filters
    const filterBtns = root.querySelectorAll('[data-spg-filter]');
    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const type = btn.dataset.spgFilter;
        root.querySelectorAll('.tab-content.active .peptide-card, #spg-peptide-grid-all .peptide-card').forEach((card) => {
          if (type === 'all') { card.style.display = ''; }
          else { card.style.display = (card.dataset.tags || '').includes(type) ? '' : 'none'; }
        });
      });
    });

    // FAQ accordion
    const faqQuestions = root.querySelectorAll('[data-spg-faq]');
    faqQuestions.forEach((q) => {
      q.addEventListener('click', () => {
        const item = q.parentElement;
        const isOpen = item.classList.contains('open');
        root.querySelectorAll('.faq-item.open').forEach((i) => i.classList.remove('open'));
        if (!isOpen) item.classList.add('open');
      });
    });

    // Form submissions
    const forms = root.querySelectorAll('[data-spg-form]');
    forms.forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formId = form.dataset.spgForm;
        const success = root.querySelector('#spg-' + formId + '-success');
        if (success) { success.style.display = 'block'; form.style.display = 'none'; }
      });
    });

    // Scroll reveal + count-up/bar-fill animations. Inline <script> blocks are
    // stripped by DOMPurify, so React re-implements the shared conventions:
    // `.reveal`→`.in`/`.visible`, `.count[data-target]`, `.fill[data-w]`.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animateCount = (el) => {
      const target = parseFloat(el.dataset.target);
      if (Number.isNaN(target) || el.dataset.done) return;
      el.dataset.done = '1';
      const dec = parseInt(el.dataset.decimals || '0', 10);
      if (reduceMotion) { el.textContent = target.toFixed(dec); return; }
      let start = null;
      const dur = 1300;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        el.textContent = (target * (1 - Math.pow(1 - p, 3))).toFixed(dec);
        if (p < 1) requestAnimationFrame(step); else el.textContent = target.toFixed(dec);
      };
      requestAnimationFrame(step);
    };
    const activate = (el) => {
      el.classList.add('visible', 'in');
      el.querySelectorAll('.count[data-target]').forEach(animateCount);
      el.querySelectorAll('.fill[data-w]').forEach((f) => { f.style.width = f.dataset.w + '%'; });
    };
    const revealEls = [
      ...root.querySelectorAll('.reveal, .fade-up, .pse-fade-in, .herostat, .statbar, .chart, .market'),
    ];
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activate(entry.target);
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
    revealEls.forEach((el) => revealObserver.observe(el));

    const revealUpTo = (targetEl) => {
      const allSections = root.querySelectorAll('.content-section, .hero');
      let found = false;
      allSections.forEach((s) => {
        if (!found) {
          activate(s);
          revealObserver.unobserve(s);
        }
        if (s === targetEl || s.contains(targetEl)) found = true;
      });
      activate(targetEl);
      revealObserver.unobserve(targetEl);
    };

    // TOC anchors
    const tocLinks = root.querySelectorAll('.toc-item a, .toc-list a');
    const tocClickHandler = (e) => {
      const href = e.currentTarget.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      e.preventDefault();
      const targetId = href.slice(1);
      const targetEl = root.querySelector('#' + CSS.escape(targetId));
      if (targetEl) {
        revealUpTo(targetEl);
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', href);
      }
    };
    tocLinks.forEach((link) => link.addEventListener('click', tocClickHandler));

    if (window.location.hash) {
      const targetId = window.location.hash.slice(1);
      const targetEl = root.querySelector('#' + CSS.escape(targetId));
      if (targetEl) {
        setTimeout(() => {
          revealUpTo(targetEl);
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    }

    // Segmented tab toggles (buttons id="tab-x" ↔ panels id="panel-x")
    const segButtons = [...root.querySelectorAll('.seg button[id^="tab-"]')];
    const segHandler = (e) => {
      const btn = e.currentTarget;
      const seg = btn.closest('.seg');
      if (!seg) return;
      seg.querySelectorAll('button[id^="tab-"]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', String(on));
        const panel = root.querySelector('#panel-' + b.id.slice(4));
        if (panel) panel.classList.toggle('on', on);
      });
    };
    segButtons.forEach((b) => b.addEventListener('click', segHandler));

    // Hide images whose src is dead (broken hotlinks) instead of leaving gaps
    const imgErrorHandler = (e) => {
      if (e.target && e.target.tagName === 'IMG') e.target.style.display = 'none';
    };
    root.addEventListener('error', imgErrorHandler, true);
    root.querySelectorAll('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && img.src) img.style.display = 'none';
    });

    // Inline scripts inside the raw HTML must be re-attached to actually run
    const scripts = root.querySelectorAll('script');
    const execScripts = [];
    scripts.forEach((oldScript) => {
      if (oldScript.type && oldScript.type !== 'text/javascript' && oldScript.type !== '') return;
      const newScript = document.createElement('script');
      if (oldScript.src) newScript.src = oldScript.src;
      else newScript.textContent = oldScript.textContent;
      oldScript.parentNode.removeChild(oldScript);
      execScripts.push(newScript);
    });
    execScripts.forEach((s) => root.appendChild(s));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      revealObserver.disconnect();
      tocLinks.forEach((link) => link.removeEventListener('click', tocClickHandler));
      segButtons.forEach((b) => b.removeEventListener('click', segHandler));
      root.removeEventListener('error', imgErrorHandler, true);
    };
  }, [article, contentRef]);
}
